import React, { useState, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_API_URL || 'https://leadgen-api.anassk.workers.dev';

function getClientId() {
  return localStorage.getItem('leadgen_client_id') || '';
}

function FlyerGenerator({ isVisible, onClose, selectedLeads, leads }) {
  const [company, setCompany] = useState({
    companyName: 'All That Roofing',
    website: 'https://allthatroofingin.com',
    logoUrl: 'https://cdn-ildjkjn.nitrocdn.com/CKHlWGVfZtGQVBeHDvKSLYGHCjhfxjwk/assets/images/optimized/allthatroofingin.com/wp-content/uploads/2025/01/ATRRoofingLogoWhiteBG.webp',
    phone: '(317) 555-ROOF',
    tagline: 'Your Trusted Roofing Experts'
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editMode, setEditMode] = useState(false);

  // Load company settings
  useEffect(() => {
    if (isVisible) {
      fetch(`${API_URL}/api/company`, {
        headers: { 'X-Client-ID': getClientId() }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.settings) {
            setCompany(data.settings);
          }
        })
        .catch(console.error);
    }
  }, [isVisible]);

  // Save company settings
  const saveCompany = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': getClientId()
        },
        body: JSON.stringify(company)
      });
      setEditMode(false);
    } catch (e) {
      console.error('Failed to save:', e);
    }
    setLoading(false);
  };

  // Get selected leads data
  const getSelectedLeadsData = useCallback(() => {
    return leads.filter((_, i) => selectedLeads.has(i));
  }, [leads, selectedLeads]);

  // Generate flyers PDF
  const generateFlyers = async () => {
    const selectedData = getSelectedLeadsData();
    if (selectedData.length === 0) return;

    setGenerating(true);
    setProgress(0);

    try {
      // Create QR codes on server (for tracking)
      const qrResponse = await fetch(`${API_URL}/api/qr/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': getClientId()
        },
        body: JSON.stringify({
          leads: selectedData,
          companyWebsite: company.website
        })
      });

      const qrData = await qrResponse.json();
      if (!qrData.success) {
        alert('Failed to create tracking codes');
        setGenerating(false);
        return;
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });

      const pageWidth = 8.5;
      const pageHeight = 11;

      // Load logo
      let logoImg = null;
      try {
        const logoResponse = await fetch(company.logoUrl);
        const logoBlob = await logoResponse.blob();
        logoImg = await blobToBase64(logoBlob);
      } catch (e) {
        console.warn('Could not load logo:', e);
      }

      for (let i = 0; i < qrData.qrCodes.length; i++) {
        const qrInfo = qrData.qrCodes[i];
        const lead = selectedData[i];

        if (i > 0) pdf.addPage();
        setProgress(Math.round(((i + 1) / qrData.qrCodes.length) * 100));

        // Generate QR code image
        const qrDataUrl = await QRCode.toDataURL(qrInfo.scanUrl, {
          width: 200,
          margin: 1,
          color: { dark: '#1e293b', light: '#ffffff' }
        });

        // Draw flyer
        await drawFlyer(pdf, {
          pageWidth,
          pageHeight,
          lead,
          qrInfo,
          qrDataUrl,
          logoImg,
          company
        });
      }

      // Save PDF
      const filename = `flyers_${new Date().toISOString().split('T')[0]}_${selectedData.length}pages.pdf`;
      pdf.save(filename);

    } catch (e) {
      console.error('Failed to generate flyers:', e);
      alert('Error generating flyers: ' + e.message);
    }

    setGenerating(false);
    setProgress(0);
  };

  if (!isVisible) return null;

  const selectedCount = selectedLeads.size;

  return (
    <div className="flyer-overlay">
      <div className="flyer-modal">
        <button className="flyer-close" onClick={onClose}>&times;</button>

        <h2>Print Flyers</h2>
        <p className="flyer-subtitle">Generate personalized flyers with QR tracking</p>

        {/* Company Settings */}
        <div className="company-section">
          <div className="company-header">
            <h3>Company Details</h3>
            {!editMode ? (
              <button className="edit-btn" onClick={() => setEditMode(true)}>Edit</button>
            ) : (
              <button className="save-btn" onClick={saveCompany} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>

          {editMode ? (
            <div className="company-form">
              <div className="form-row">
                <label>Company Name</label>
                <input
                  value={company.companyName}
                  onChange={(e) => setCompany({ ...company, companyName: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Website URL</label>
                <input
                  value={company.website}
                  onChange={(e) => setCompany({ ...company, website: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Logo URL</label>
                <input
                  value={company.logoUrl}
                  onChange={(e) => setCompany({ ...company, logoUrl: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Phone</label>
                <input
                  value={company.phone}
                  onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Tagline</label>
                <input
                  value={company.tagline}
                  onChange={(e) => setCompany({ ...company, tagline: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <div className="company-preview">
              <div className="company-logo">
                <img src={company.logoUrl} alt="Logo" onError={(e) => e.target.style.display = 'none'} />
              </div>
              <div className="company-info">
                <strong>{company.companyName}</strong>
                <span>{company.phone}</span>
                <span>{company.website}</span>
              </div>
            </div>
          )}
        </div>

        {/* Selected Leads */}
        <div className="selected-section">
          <h3>Selected Leads: {selectedCount}</h3>
          {selectedCount === 0 ? (
            <p className="no-leads">No leads selected. Go back and select some addresses.</p>
          ) : (
            <div className="leads-preview">
              {getSelectedLeadsData().slice(0, 5).map((lead, i) => (
                <div key={i} className="lead-preview-item">
                  <strong>{lead.ownerName || 'Homeowner'}</strong>
                  <span>{lead.streetAddress}, {lead.city}</span>
                </div>
              ))}
              {selectedCount > 5 && (
                <div className="more-leads">+ {selectedCount - 5} more...</div>
              )}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="generate-section">
          {generating ? (
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span>Generating... {progress}%</span>
            </div>
          ) : (
            <button
              className="generate-btn"
              onClick={generateFlyers}
              disabled={selectedCount === 0}
            >
              Generate {selectedCount} Flyer{selectedCount !== 1 ? 's' : ''} (PDF)
            </button>
          )}
          <p className="generate-note">
            Each flyer includes a unique QR code. When scanned, you'll get a Slack notification with the homeowner's details!
          </p>
        </div>
      </div>
    </div>
  );
}

// ATR Roofing company data
const ATR_DATA = {
  name: 'ATR Roofing',
  owner: 'Kyle Williams',
  phone: '(317) 460-1191',
  address: '6075 E County Rd 200 N, Avon, IN 46123',
  website: 'allthatroofingin.com',
  rating: '4.9',
  reviews: '170',
  years: '30'
};

// Helper: Draw a single flyer page - Professional Letter Style (VERSION B)
async function drawFlyer(pdf, { pageWidth, pageHeight, lead, qrInfo, qrDataUrl, logoImg, company }) {
  const margin = 0.75;
  const contentWidth = pageWidth - margin * 2;
  const centerX = pageWidth / 2;

  // White background
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');

  // Top blue accent bar
  pdf.setFillColor(30, 64, 175);
  pdf.rect(0, 0, pageWidth, 0.12, 'F');

  // Header section with logo
  let currentY = 0.5;

  // Logo - properly sized (0.8" x 0.8")
  if (logoImg) {
    try {
      pdf.addImage(logoImg, 'PNG', margin, currentY, 0.8, 0.8);
    } catch (e) { console.warn('Logo failed:', e); }
  }

  // Company name and rating next to logo
  pdf.setTextColor(30, 41, 59);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(ATR_DATA.name, margin + 1, currentY + 0.35);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 116, 139);
  pdf.text(`${ATR_DATA.rating} Rating | ${ATR_DATA.reviews} Reviews | ${ATR_DATA.years} Years Serving Indianapolis`, margin + 1, currentY + 0.6);

  currentY = 1.5;

  // Thin divider
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.01);
  pdf.line(margin, currentY, pageWidth - margin, currentY);

  currentY += 0.3;

  // Personalized greeting
  const ownerName = lead.ownerName || 'Neighbor';
  const firstName = ownerName.split(' ')[0].split(',')[0].replace(/[^a-zA-Z]/g, '') || 'Neighbor';

  pdf.setTextColor(30, 41, 59);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Dear ${firstName},`, margin, currentY);

  currentY += 0.35;

  // Letter body - professional copywriting
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(55, 65, 81);

  const letterLines = [
    `I'm Kyle Williams, owner of ATR Roofing. For ${ATR_DATA.years} years, we've helped`,
    `Indianapolis-area homeowners protect their homes with roof systems`,
    `that look great and hold up in Indiana weather.`,
    ``,
    `I noticed your beautiful home at ${lead.streetAddress}. If your roof`,
    `is 15+ years old, or you've noticed granules in gutters, lifted shingles,`,
    `or dark streaks, it's worth checking before small issues become costly.`,
  ];

  letterLines.forEach(line => {
    pdf.text(line, margin, currentY);
    currentY += 0.24;
  });

  currentY += 0.15;

  // What we offer box - compact design
  pdf.setFillColor(240, 253, 244);
  pdf.setDrawColor(34, 197, 94);
  pdf.setLineWidth(0.015);
  pdf.roundedRect(margin, currentY, contentWidth, 0.9, 0.06, 0.06, 'FD');

  pdf.setTextColor(22, 101, 52);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text("Here's what we'll do (FREE):", margin + 0.15, currentY + 0.2);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  const benefits = [
    '27-Point Roof Inspection + Photo Report',
    'Clear recommendation: repair vs. replacement (no guessing)',
    'Insurance claim help if storm damage is involved'
  ];
  let benefitY = currentY + 0.38;
  benefits.forEach(b => {
    pdf.text(`✓ ${b}`, margin + 0.15, benefitY);
    benefitY += 0.17;
  });

  currentY += 1.05;

  // QR Code section - clean design
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(margin, currentY, contentWidth, 1.6, 0.06, 0.06, 'F');

  // QR Code - left aligned, 1" size
  const qrSize = 1.0;
  pdf.addImage(qrDataUrl, 'PNG', margin + 0.2, currentY + 0.3, qrSize, qrSize);

  // Text next to QR
  pdf.setTextColor(30, 41, 59);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Scan to Request Your Free Inspection', margin + 1.4, currentY + 0.55);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 116, 139);
  pdf.text('Choose a time → We inspect → You get a photo report', margin + 1.4, currentY + 0.8);
  pdf.text('No pressure. No obligation. Just clear answers.', margin + 1.4, currentY + 1.0);

  pdf.setFontSize(10);
  pdf.setTextColor(30, 64, 175);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Or call/text: ${ATR_DATA.phone}`, margin + 1.4, currentY + 1.3);

  currentY += 1.8;

  // Signature
  pdf.setTextColor(30, 41, 59);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Sincerely,', margin, currentY);
  currentY += 0.25;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Kyle Williams', margin, currentY);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  currentY += 0.2;
  pdf.text('Owner, ATR Roofing', margin, currentY);

  // Footer bar
  const footerY = pageHeight - 0.7;
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, footerY, pageWidth, 0.7, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${ATR_DATA.address}  |  ${ATR_DATA.phone}  |  ${ATR_DATA.website}`, centerX, footerY + 0.28, { align: 'center' });

  pdf.setFontSize(8);
  pdf.setTextColor(156, 163, 175);
  pdf.text('GAF Master Elite® Certified  •  Open 24 Hours  •  Free Estimates', centerX, footerY + 0.5, { align: 'center' });
}

// Helper: Convert blob to base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default FlyerGenerator;
